import type { IFormField } from "@/types/app";
import ReactSelect from "react-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import type { Control } from "react-hook-form";

interface SelectOption {
  value: string;
  label: string;
}

interface Props extends IFormField {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  options?: SelectOption[];
}

const SelectField = ({
  label,
  name,
  disabled,
  control,
  placeholder,
  options = [],
  required,
  isSearchable = false,
}: Props) => {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedOption =
          options.find((option) => option.value === field.value) ?? null;

        return (
          <FormItem className={cn("flex", "flex-col", "gap-2")}>
            {label && (
              <FormLabel
                className={cn("text-sm", "font-medium", "text-foreground")}
              >
                {label}{" "}
                {required && <span className="text-destructive">*</span>}
              </FormLabel>
            )}
            <FormControl>
              {isSearchable ? (
                <ReactSelect
                  isRtl
                  isSearchable
                  options={options}
                  value={selectedOption}
                  onChange={(newValue) =>
                    field.onChange(
                      (newValue as SelectOption | null)?.value ?? "",
                    )
                  }
                  onBlur={field.onBlur}
                  isDisabled={disabled}
                  placeholder={placeholder || "اختر..."}
                  noOptionsMessage={() => "لا توجد خيارات متاحة"}
                  classNamePrefix="react-select"
                  className="text-sm"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      backgroundColor: "var(--background)",
                      borderColor: state.isFocused
                        ? "var(--primary)"
                        : "var(--border)",
                      boxShadow: state.isFocused
                        ? "0 0 0 1px var(--primary)"
                        : "none",
                      "&:hover": {
                        borderColor: "var(--primary)",
                      },
                      borderRadius: "0.5rem",
                      minHeight: "2.5rem",
                      transition: "all 0.2s",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "var(--popover)",
                      border: "1px solid var(--border)",
                      borderRadius: "0.5rem",
                      boxShadow:
                        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                      overflow: "hidden",
                      zIndex: 50,
                      marginTop: "4px",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? "var(--primary)"
                        : state.isFocused
                          ? "var(--accent)"
                          : "transparent",
                      color: state.isSelected
                        ? "var(--primary-foreground)"
                        : state.isFocused
                          ? "var(--accent-foreground)"
                          : "var(--popover-foreground)",
                      "&:active": {
                        backgroundColor: "var(--primary)",
                        color: "var(--primary-foreground)",
                      },
                      cursor: "pointer",
                      padding: "8px 12px",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "var(--placeholder)",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "var(--foreground)",
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: "var(--foreground)",
                    }),
                    indicatorSeparator: () => ({
                      display: "none",
                    }),
                    dropdownIndicator: (base, state) => ({
                      ...base,
                      color: state.isFocused
                        ? "var(--primary)"
                        : "var(--muted-foreground)",
                      "&:hover": {
                        color: "var(--primary)",
                      },
                    }),
                    clearIndicator: (base) => ({
                      ...base,
                      color: "var(--muted-foreground)",
                      "&:hover": {
                        color: "var(--destructive)",
                      },
                    }),
                  }}
                />
              ) : (
                <Select
                  value={
                    field.value !== undefined &&
                    field.value !== null &&
                    field.value !== ""
                      ? String(field.value)
                      : undefined
                  }
                  onValueChange={field.onChange}
                  disabled={disabled}
                >
                  <SelectTrigger
                    className={cn(
                      "h-10 transition-all",
                      "focus:ring-1 focus:ring-primary",
                    )}
                  >
                    <SelectValue placeholder={placeholder} />
                  </SelectTrigger>
                  <SelectContent>
                    {options.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        className="cursor-pointer"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </FormControl>
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
};

export default SelectField;
